import { OrgStructure } from '@commenda/api';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useStore,
  type CoordinateExtent,
  type EdgeTypes,
  type FitViewOptions,
  type NodeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ChartMinimap,
  ChartViewportSync,
  ReadableViewportHelper,
  useReadableFitView,
} from '../ChartViewport';
import { DiagramToolbar } from '../OrgDiagram/components/DiagramToolbar';
import { EmptyState } from '../OrgDiagram/components/EmptyState';
import { OwnershipEdge } from '../OrgDiagram/components/OwnershipEdge';
import { UnlinkedEntitiesPanel } from '../OrgDiagram/components/UnlinkedEntitiesPanel';
import { buildGraph } from '../OrgDiagram/graph/buildGraph';
import type { EntityNode as EntityNodeType } from '../OrgDiagram/graph/types';
import { TaxChartContextProvider } from './TaxChartContext';
import { TaxChartLegend } from './components/TaxChartLegend';
import { TaxChartNode } from './components/TaxChartNode';
import { copyTaxChartPngToClipboard, exportTaxChartPdf, exportTaxChartPng } from './exportTaxChart';
import { layoutGraph, TAX_NODE_HEIGHT, TAX_NODE_WIDTH } from './layout';

interface TaxDiagramProps {
  structure: OrgStructure;
  companyId: number;
  companyName?: string;
  onEntityClick?: (entityId: number) => void;
  onUnlinkedClick?: (entityId: number) => void;
  onExportPng?: () => void;
  onExportPdf?: () => void;
  onCopyPng?: () => void;
  topLeftSlot?: ReactNode;
  topRightSlot?: ReactNode;
  hideUnlinkedPanel?: boolean;
  onReady?: (rf: ReactFlowInstance) => void;
  hideToolbar?: boolean;
}

function CaptureGate({ onReady }: { onReady: (rf: ReactFlowInstance) => void }) {
  const rf = useReactFlow();
  const nodeCount = useStore((s) => s.nodeLookup.size);
  const calledRef = useRef(false);
  useEffect(() => {
    if (calledRef.current || nodeCount === 0) return;
    // Two RAFs: first commits node DOM, second lets the initial fit
    // tick through. The capture doesn't depend on the viewport transform
    // (we re-translate via PADDING) but waiting avoids racing a half-laid-out
    // tree on dense graphs.
    const f1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (calledRef.current) return;
        calledRef.current = true;
        onReady(rf);
      });
    });
    return () => cancelAnimationFrame(f1);
  }, [onReady, nodeCount, rf]);
  return null;
}

const NODE_TYPES: NodeTypes = { entity: TaxChartNode };
const EDGE_TYPES: EdgeTypes = { ownership: OwnershipEdge };

const PAN_OVERSCROLL_FRACTION = 0.5;

// Distance of the legend from the canvas' bottom-left corner, plus the
// breathing room we keep between it and the nearest node.
const LEGEND_INSET = 12;
const LEGEND_GAP = 12;
const BASE_FIT_PADDING = 0.15;
const TAX_NODE_SIZE = { width: TAX_NODE_WIDTH, height: TAX_NODE_HEIGHT };

interface Size {
  width: number;
  height: number;
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function computeBounds(nodes: EntityNodeType[]): Bounds | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + TAX_NODE_WIDTH);
    maxY = Math.max(maxY, n.position.y + TAX_NODE_HEIGHT);
  }
  return { minX, minY, maxX, maxY };
}

function computeTranslateExtent(
  bounds: Bounds | null,
  canvasSize: Size | null,
): CoordinateExtent {
  if (!bounds) {
    return [
      [-Infinity, -Infinity],
      [Infinity, Infinity],
    ];
  }
  const padX = Math.max(100, (canvasSize?.width ?? 0) * PAN_OVERSCROLL_FRACTION);
  const padY = Math.max(100, (canvasSize?.height ?? 0) * PAN_OVERSCROLL_FRACTION);
  return [
    [bounds.minX - padX, bounds.minY - padY],
    [bounds.maxX + padX, bounds.maxY + padY],
  ];
}

// Reserves the space the bottom-left legend occupies so fitView never lands a
// node underneath it. Reserving the legend's column and reserving its row both
// clear the overlap, so we keep whichever one leaves the chart larger.
function computeFitPadding(
  bounds: Bounds | null,
  canvasSize: Size | null,
  legendSize: Size | null,
): FitViewOptions['padding'] {
  if (!bounds || !canvasSize || !legendSize) return BASE_FIT_PADDING;
  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  if (contentWidth <= 0 || contentHeight <= 0) return BASE_FIT_PADDING;

  const basePadX = (canvasSize.width - canvasSize.width / (1 + BASE_FIT_PADDING)) * 0.5;
  const basePadY = (canvasSize.height - canvasSize.height / (1 + BASE_FIT_PADDING)) * 0.5;
  const reserveX = Math.max(legendSize.width + LEGEND_INSET + LEGEND_GAP - basePadX, 0);
  const reserveY = Math.max(legendSize.height + LEGEND_INSET + LEGEND_GAP - basePadY, 0);

  const zoomFor = (extraX: number, extraY: number) =>
    Math.min(
      (canvasSize.width - 2 * basePadX - extraX) / contentWidth,
      (canvasSize.height - 2 * basePadY - extraY) / contentHeight,
    );

  const reserveColumn = zoomFor(reserveX, 0) >= zoomFor(0, reserveY);
  return {
    top: `${basePadY}px`,
    right: `${basePadX}px`,
    bottom: `${basePadY + (reserveColumn ? 0 : reserveY)}px`,
    left: `${basePadX + (reserveColumn ? reserveX : 0)}px`,
  };
}

function ToolbarWithExport({
  nodes,
  companyId,
  companyName,
  onExportPngOverride,
  onExportPdfOverride,
  onCopyPngOverride,
  fitViewPadding,
}: {
  nodes: EntityNodeType[];
  companyId: number;
  companyName?: string;
  onExportPngOverride?: () => void;
  onExportPdfOverride?: () => void;
  onCopyPngOverride?: () => void;
  fitViewPadding: FitViewOptions['padding'];
}) {
  const rf = useReactFlow();
  const handlePng = useCallback(() => {
    if (onExportPngOverride) return onExportPngOverride();
    void exportTaxChartPng(rf, companyName);
  }, [onExportPngOverride, rf, companyName]);
  const handlePdf = useCallback(() => {
    if (onExportPdfOverride) return onExportPdfOverride();
    void exportTaxChartPdf(rf, companyId, companyName);
  }, [onExportPdfOverride, rf, companyId, companyName]);
  const handleCopy = useCallback(() => {
    if (onCopyPngOverride) return onCopyPngOverride();
    copyTaxChartPngToClipboard(rf).catch((err) => {
      console.error('Failed to copy chart to clipboard', err);
    });
  }, [onCopyPngOverride, rf]);
  const fitReadableView = useReadableFitView({
    nodes,
    nodeSize: TAX_NODE_SIZE,
    padding: fitViewPadding,
    minZoom: ReadableViewportHelper.TAX_MIN_ZOOM,
  });
  const handleFitView = useCallback(() => fitReadableView({ duration: 250 }), [fitReadableView]);
  return (
    <DiagramToolbar
      onExportPng={handlePng}
      onExportPdf={handlePdf}
      onCopyPng={handleCopy}
      onFitView={handleFitView}
    />
  );
}

const TaxDiagram = ({
  structure,
  companyId,
  companyName,
  onEntityClick,
  onUnlinkedClick,
  onExportPng,
  onExportPdf,
  onCopyPng,
  topLeftSlot,
  topRightSlot,
  hideUnlinkedPanel,
  onReady,
  hideToolbar,
}: TaxDiagramProps) => {
  const { positionedNodes, edges, unlinked } = useMemo(() => {
    const graph = buildGraph(structure);
    const laidOut = layoutGraph(graph.nodes, graph.edges);
    return {
      positionedNodes: laidOut.nodes,
      edges: laidOut.edges,
      unlinked: graph.unlinked,
    };
  }, [structure]);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState<Size | null>(null);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const sync = () => setCanvasSize({ width: el.clientWidth, height: el.clientHeight });
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Measured rather than hardcoded so the reserved space tracks the legend's
  // real size (label lengths, fonts). We keep the largest size seen so the
  // chart doesn't re-fit while the legend accordion animates closed.
  const legendRef = useRef<HTMLDivElement>(null);
  const [legendSize, setLegendSize] = useState<Size | null>(null);
  useEffect(() => {
    const el = legendRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setLegendSize((prev) => {
        const width = Math.max(prev?.width ?? 0, el.offsetWidth);
        const height = Math.max(prev?.height ?? 0, el.offsetHeight);
        if (prev && prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hideToolbar, positionedNodes.length]);

  const bounds = useMemo(() => computeBounds(positionedNodes), [positionedNodes]);

  const translateExtent = useMemo(
    () => computeTranslateExtent(bounds, canvasSize),
    [bounds, canvasSize],
  );

  const fitPadding = useMemo(
    () => computeFitPadding(bounds, canvasSize, legendSize),
    [bounds, canvasSize, legendSize],
  );

  const zoomRange = useMemo(
    () =>
      ReadableViewportHelper.getZoomRange({
        nodePositions: positionedNodes.map((n) => n.position),
        nodeSize: TAX_NODE_SIZE,
        canvasSize: canvasSize ?? { width: 0, height: 0 },
        padding: fitPadding,
        minZoom: ReadableViewportHelper.TAX_MIN_ZOOM,
      }),
    [positionedNodes, canvasSize, fitPadding]
  );

  const taxChartContextValue = useMemo(() => ({ companyId, canOverride: !hideToolbar }), [companyId, hideToolbar]);

  return (
    <div className="flex h-full w-full flex-col gap-xl">
      <div
        ref={canvasRef}
        className="tax-diagram-canvas relative flex-1 overflow-hidden rounded-sm border-[0.5px] border-border-primary bg-bg-light shadow-[inset_0px_2px_8px_0px_rgba(0,0,0,0.04)]"
      >
        <style>{`
          .tax-diagram-canvas .react-flow__edge,
          .tax-diagram-canvas .react-flow__edge-path,
          .tax-diagram-canvas .react-flow__edge-interaction {
            cursor: grab;
          }
          .tax-diagram-canvas .react-flow__edge:active,
          .tax-diagram-canvas .react-flow__edge-path:active,
          .tax-diagram-canvas .react-flow__edge-interaction:active {
            cursor: grabbing;
          }
        `}</style>
        <TaxChartContextProvider value={taxChartContextValue}>
        <ReactFlowProvider>
          {positionedNodes.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <ChartViewportSync
                nodes={positionedNodes}
                nodeSize={TAX_NODE_SIZE}
                padding={fitPadding}
                minZoom={ReadableViewportHelper.TAX_MIN_ZOOM}
              />
              <ReactFlow
                nodes={positionedNodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                translateExtent={translateExtent}
                minZoom={zoomRange.minZoom}
                maxZoom={zoomRange.maxZoom}
                nodesDraggable={false}
                nodesConnectable={false}
                edgesFocusable={false}
                elementsSelectable={Boolean(onEntityClick)}
                panOnScroll
                panOnScrollSpeed={1}
                proOptions={{ hideAttribution: true }}
                onNodeClick={(_e, node) => {
                  if ((node.data as EntityNodeType['data'])?.isNonControlledEntity) return;
                  onEntityClick?.(Number(node.id));
                }}
                style={{
                  background: 'transparent',
                  // fg-quaternary — matches the TaxChartNode shape stroke so
                  // edges, edge labels (via OwnershipEdge CSS-var fallback)
                  // and node outlines read with a unified pen weight.
                  ['--ownership-edge-stroke' as string]: '#8F8B85',
                  ['--xy-edge-stroke' as string]: '#8F8B85',
                }}
              />
              {!hideToolbar && (
                <ChartMinimap
                  nodes={positionedNodes}
                  nodeSize={TAX_NODE_SIZE}
                />
              )}
              {!hideToolbar && (
                <ToolbarWithExport
                  nodes={positionedNodes}
                  companyId={companyId}
                  companyName={companyName}
                  onExportPngOverride={onExportPng}
                  onExportPdfOverride={onExportPdf}
                  onCopyPngOverride={onCopyPng}
                  fitViewPadding={fitPadding}
                />
              )}
              {/* In-view legend; the static legend in PNG/PDF exports is
                  composited in by captureTaxChart, so it must not render
                  during offscreen captures (hideToolbar=true). */}
              {!hideToolbar && (
                <div ref={legendRef} className="absolute z-10" style={{ bottom: LEGEND_INSET, left: LEGEND_INSET }}>
                  <TaxChartLegend />
                </div>
              )}
              {onReady && <CaptureGate onReady={onReady} />}
            </>
          )}
          {!hideUnlinkedPanel && (
            <UnlinkedEntitiesPanel
              entities={unlinked}
              onEntityClick={onUnlinkedClick ?? onEntityClick}
            />
          )}
          {topLeftSlot && (
            <div className="absolute z-10" style={{ top: 12, left: 12 }}>
              {topLeftSlot}
            </div>
          )}
          {topRightSlot && (
            <div className="absolute z-10" style={{ top: 12, right: 12 }}>
              {topRightSlot}
            </div>
          )}
        </ReactFlowProvider>
        </TaxChartContextProvider>
      </div>
    </div>
  );
};

export { TaxDiagram };
